import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormGroup, FormBuilder, Validators, AbstractControl, FormControl } from "@angular/forms";
import { Subscription } from "rxjs";
import { tap } from "rxjs/operators";
import { BaseControlComponent } from "../../../../../../../form-controls/components/base-control/base-control.component";
import { ContextInteractionService } from "../../../../../../../form-controls/services/context-interaction.service";
import { EntityBuilderService } from "../../../../../../services/entity-builder.service";
import { LunaEntity, EntityMap, PropertyMap } from "../../../../../../models/entity.model";
import { SidePanelService } from "../../../../../../../common/services/side-panel.service";
import { deepClone } from "../../../../../../models/editor.model";
import { ParserPropertyModel } from "./parser.model";

@Component({
    selector: 'app-fileparser-data-config',
    templateUrl: './fileparser-data-config.component.html',
    styleUrls: ['./fileparser-data-config.component.scss'],
    standalone: false
})
export class FileParserDataConfigComponent extends BaseControlComponent implements OnInit, OnDestroy {
    static componentName = "FileParserDataConfigComponent";
    _allEntities: LunaEntity[] = [];
    _defaultEntity: LunaEntity = {
        id: "-1",
        entityName: "--Select an Entity--",
        schemaObject: null
    } as LunaEntity;
    _storedEntities: LunaEntity[] = [];

    map: EntityMap = null;

    properties: PropertyMap[] = [];

    propertyInfoText: string = "Only properties defined as arrays can be read through file parsing.";

    subscription: Subscription = new Subscription();

    get allEntities(): LunaEntity[] {
        if (this._allEntities.length <= 1) {
            for (let e = 0; e < this._storedEntities.length; e++) {
                const index = this._allEntities.findIndex(a => a.id === this._storedEntities[e].id);
                if (index === -1) {
                    this._allEntities.push(this._storedEntities[e]);
                }
            }
            if (this._allEntities.findIndex(e => e.id === "-1") === -1) {
                this._allEntities.unshift(this._defaultEntity);
            }
        }
        return this._allEntities;
    }

    get entity(): string {
        if (this.formGroup == null) return this._defaultEntity.id;
        let entityCtrl = this.formGroup.get("entity");
        return entityCtrl.value;
    }

    get parser(): ParserPropertyModel[] {
        if (!this.componentData) this.componentData = { config: {} };
        if (!this.componentData.config) this.componentData.config = { parser: [] }
        if (!this.componentData.config.parser) this.componentData.config.parser = [];
        return this.componentData.config.parser;
    }

    set parser(value: ParserPropertyModel[]) {
        if (!this.componentData) this.componentData = { config: {} };
        if (!this.componentData.config) this.componentData.config = { parser: value }
        if (!this.componentData.config.parser) this.componentData.config.parser = value;
        this.componentData.config.parser = value;
    }

    get updatedParser() : ParserPropertyModel[] {
        if (!this.formGroup) [];
        let parserCtrl = this.formGroup.get('parser');
        if (!parserCtrl) return [];
        return <ParserPropertyModel[]>parserCtrl.value;        
    }

    get parseControl(): AbstractControl {
        if (!this.formGroup) return new FormControl({});
        return this.formGroup.get("parser");
    }

    constructor(svc: ContextInteractionService, private _builder: FormBuilder, private _entityService: EntityBuilderService, private _panelService: SidePanelService) {
        super(svc);
    }

    ngOnInit() {
        
        const ent$ = this._entityService.getActive().pipe(tap((data: LunaEntity[]) => {
            this._storedEntities = data;
        })).subscribe(
            () => { console.log("entities loaded"); },
            (error) => { console.error(error); }
        );
        this.subscription.add(ent$);

        this.formGroup = this.buildForm();
        let entityCtrl = this.formGroup.get("entity");
        if (entityCtrl != null) {
            let val$ = entityCtrl.valueChanges.pipe()
                .subscribe(
                    (value: any) => {                        
                        if (value == null || value == "-1") {
                            this.map = null;
                            this.properties = [];
                        } else {
                            const ent: LunaEntity = this._allEntities.find(e => e.id === value);
                            this.map = this._entityService.generateEntityMap(ent);
                            this.properties = this.map.propertyMaps.filter(p => p.allowMultipleMaps);
                        }
                    },
                    (error) => { console.error(error); }
            );
            this.subscription.add(val$);
        }
    }

    ngOnDestroy() {
        if (this.subscription) this.subscription.unsubscribe();
    }

    addToParser(property: ParserPropertyModel): void {
        if (this.formGroup != null) {
            const parserCtrl = this.formGroup.get("parser");
            if (parserCtrl != null) {
                const arrVal = <ParserPropertyModel[]>parserCtrl.value;
                arrVal.push(property);
                parserCtrl.setValue(arrVal);
                parserCtrl.markAsDirty();
            }
        }
    }

    buildForm(): FormGroup {
        const stored = deepClone(this.parser);
        return this._builder.group({
            entity: [this.entity, [Validators.required]],
            parser: [stored, [Validators.required, Validators.min(1)]]
        });
    }

    cancel(): void {
        this.formGroup.reset();
        this._panelService.toggleSidePanel(false);
    }

    deleteProperty(index: number): void {
        let value = this.parseControl.value;
        if (Array.isArray(value)) {
            value.splice(index, 1);
            this.parseControl.setValue(value);
        }
    }
    

    save(): void {
        if (this.formGroup != null) {
            const parserCtrl = this.formGroup.get("parser");
            if (parserCtrl != null) {
                this.parser = <ParserPropertyModel[]>parserCtrl.value;           
            }
            this._panelService.toggleSidePanel(false);
        }
    }
}
